import { expenseService } from './api';

/**
 * Offline queue for new expenses. Receipts captured without a connection are
 * stored in IndexedDB (fields + image blob) and uploaded when back online.
 * Each entry carries an idempotency key so a retried upload can never create
 * the same expense twice on the server.
 */
const DB_NAME = 'expense-tracker';
const DB_VERSION = 1;
const STORE = 'pending-expenses';

const listeners = new Set();
let syncing = null;

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore(mode, fn) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    let result;
    Promise.resolve(fn(store)).then(r => { result = r; });
    tx.oncomplete = () => { db.close(); resolve(result); };
    tx.onerror = () => { db.close(); reject(tx.error); };
    tx.onabort = () => { db.close(); reject(tx.error); };
  });
}

function requestToPromise(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// Entries are tagged with the user who captured them so a different user
// logging in on the same device never uploads (or sees) them
function currentUserId() {
  try {
    return JSON.parse(localStorage.getItem('auth_user'))?.id ?? null;
  } catch {
    return null;
  }
}

function notify() {
  listeners.forEach(fn => fn());
}

export function newIdempotencyKey() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
}

/** True when an API error means "no connection" rather than a server answer. */
export function isOfflineError(error) {
  if (!navigator.onLine) return true;
  if (!error.response) return true; // network error, request never answered
  // The service worker answers API calls with 503 + { error: 'Offline' } when offline
  return error.response.status === 503 && error.response.data?.error === 'Offline';
}

export async function enqueueExpense(data, file, idempotencyKey) {
  const entry = {
    id: idempotencyKey,
    userId: currentUserId(),
    data,
    // Store as Blob + name: File objects are not cloneable into IndexedDB on all browsers
    file: new Blob([file], { type: file.type }),
    fileName: file.name,
    createdAt: new Date().toISOString(),
    error: null,
  };
  await withStore('readwrite', store => store.put(entry));
  notify();
  return entry;
}

/** Queued expenses of the logged-in user, newest first. */
export async function listPending() {
  try {
    const entries = await withStore('readonly', store => requestToPromise(store.getAll()));
    const userId = currentUserId();
    return (entries || [])
      .filter(e => e.userId === userId)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  } catch (err) {
    console.warn('Could not read offline queue:', err);
    return [];
  }
}

export async function removePending(id) {
  await withStore('readwrite', store => store.delete(id));
  notify();
}

async function markError(entry, message) {
  await withStore('readwrite', store => store.put({ ...entry, error: message }));
}

/**
 * Upload every queued expense. Stops at the first connectivity failure;
 * entries the server rejects are kept and flagged with the error.
 * Returns the number of expenses uploaded.
 */
export function syncPending() {
  if (syncing) return syncing;
  syncing = (async () => {
    let uploaded = 0;
    try {
      if (!navigator.onLine || !localStorage.getItem('auth_token')) return 0;
      const entries = await listPending();
      for (const entry of entries.reverse()) {
        try {
          const file = new File([entry.file], entry.fileName, { type: entry.file.type });
          await expenseService.createExpense(entry.data, file, entry.id);
          await removePending(entry.id);
          uploaded++;
        } catch (error) {
          if (isOfflineError(error)) break;
          const message = error.response?.data?.detail || 'Upload failed';
          await markError(entry, typeof message === 'string' ? message : 'Upload failed');
        }
      }
    } finally {
      syncing = null;
      notify();
    }
    return uploaded;
  })();
  return syncing;
}

/** Subscribe to queue changes. Returns an unsubscribe function. */
export function subscribePending(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
